import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  inject,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import {
  ChevronDown,
  ChevronUp,
  LucideAngularModule,
  MapPin,
  X,
} from 'lucide-angular';
import { AssignmentService } from '../../services/assignment.service';
import { ASSIGNMENT_THEME_COLOR } from '../../services/assignment.models';
import { MetadataApiService } from '../../../../../common/services/metadata-api.service';
import { FeatureFlagsService } from '../../../../../common/services/feature-flags.service';
import { AuthService } from '../../../../../common/auth/auth.service';
import { AmplitudeService } from '../../../../../common/analytics/amplitude.service';
import {
  Grade,
  Tier1Subject,
  Tier2Subject,
  HIDDEN_GRADE_VALUES,
  US_STATES,
  UsState,
} from '../../../../../common/services/metadata.models';

interface FilteredTier1Subject extends Tier1Subject {
  isRelevant: boolean;
}

interface FilteredTier2Subject extends Tier2Subject {
  isRelevant: boolean;
}

@Component({
  selector: 'app-step-context',
  standalone: true,
  imports: [CommonModule, FormsModule, LucideAngularModule],
  templateUrl: './step-context.html',
  styleUrl: './step-context.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StepContextComponent implements OnInit {
  private readonly assignmentService = inject(AssignmentService);
  private readonly metadataApiService = inject(MetadataApiService);
  private readonly featureFlagsService = inject(FeatureFlagsService);
  private readonly authService = inject(AuthService);
  private readonly amplitudeService = inject(AmplitudeService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  @Output() readonly next = new EventEmitter<void>();
  @ViewChild('stateCombobox') stateComboboxRef!: ElementRef<HTMLDivElement>;

  protected readonly themeColor = ASSIGNMENT_THEME_COLOR;
  protected readonly inputData$ = this.assignmentService.inputData$;
  protected readonly error$ = this.assignmentService.error$;
  protected readonly isAuthenticated$ = this.authService.isAuthenticated();
  protected readonly enableAdvancedContextFields$ =
    this.featureFlagsService.enableAdvancedContextFields$;

  protected readonly usStates = US_STATES;

  protected readonly icons = {
    ChevronDown,
    ChevronUp,
    X,
    MapPin,
  };

  // ============================================
  // METADATA (from API)
  // ============================================

  private readonly grades$$ = new BehaviorSubject<Grade[]>([]);
  protected readonly grades$ = this.grades$$.asObservable();

  private readonly tier1Subjects$$ = new BehaviorSubject<Tier1Subject[]>([]);

  // ============================================
  // GRADE TOGGLE (Show more K-5)
  // ============================================

  private readonly showMoreGrades$$ = new BehaviorSubject<boolean>(false);
  protected readonly showMoreGrades$ = this.showMoreGrades$$.asObservable();

  protected readonly visibleGrades$: Observable<Grade[]> = combineLatest([
    this.grades$,
    this.showMoreGrades$,
  ]).pipe(
    map(([grades, showMore]) => {
      if (showMore) {
        return grades;
      }
      return grades.filter((g) => !HIDDEN_GRADE_VALUES.includes(g.value));
    }),
  );

  protected readonly hasHiddenGrades$: Observable<boolean> = this.grades$.pipe(
    map((grades) => grades.some((g) => HIDDEN_GRADE_VALUES.includes(g.value))),
  );

  // ============================================
  // TIER 1 SUBJECTS (Show more toggle)
  // ============================================

  private readonly showMoreTier1$$ = new BehaviorSubject<boolean>(false);
  protected readonly showMoreTier1$ = this.showMoreTier1$$.asObservable();

  protected readonly filteredTier1Subjects$: Observable<FilteredTier1Subject[]> =
    combineLatest([this.tier1Subjects$$, this.inputData$]).pipe(
      map(([tier1Subjects, inputData]) => {
        const selectedGrade = inputData.grade;
        return tier1Subjects.map((tier1) => ({
          ...tier1,
          isRelevant:
            selectedGrade !== null &&
            tier1.subjects.some((s) =>
              s.applicableGrades.includes(selectedGrade),
            ),
        }));
      }),
    );

  protected readonly visibleTier1Subjects$: Observable<FilteredTier1Subject[]> =
    combineLatest([this.filteredTier1Subjects$, this.showMoreTier1$]).pipe(
      map(([subjects, showMore]) => {
        if (showMore) {
          return [...subjects].sort(
            (a, b) => (b.isRelevant ? 1 : 0) - (a.isRelevant ? 1 : 0),
          );
        }
        return subjects.filter((s) => s.isRelevant);
      }),
    );

  protected readonly hasHiddenTier1$: Observable<boolean> =
    this.filteredTier1Subjects$.pipe(
      map((subjects) => subjects.some((s) => !s.isRelevant)),
    );

  // ============================================
  // TIER 2 SUBJECTS (Show more toggle)
  // ============================================

  private readonly showMoreTier2$$ = new BehaviorSubject<boolean>(false);
  protected readonly showMoreTier2$ = this.showMoreTier2$$.asObservable();

  protected readonly filteredTier2Subjects$: Observable<FilteredTier2Subject[]> =
    combineLatest([this.tier1Subjects$$, this.inputData$]).pipe(
      map(([tier1Subjects, inputData]) => {
        const selectedTier1 = tier1Subjects.find(
          (t) => t.id === inputData.tier1SubjectId,
        );
        if (!selectedTier1) {
          return [];
        }
        const selectedGrade = inputData.grade;
        return selectedTier1.subjects.map((subject) => ({
          ...subject,
          isRelevant:
            selectedGrade !== null &&
            subject.applicableGrades.includes(selectedGrade),
        }));
      }),
    );

  protected readonly visibleTier2Subjects$: Observable<FilteredTier2Subject[]> =
    combineLatest([this.filteredTier2Subjects$, this.showMoreTier2$]).pipe(
      map(([subjects, showMore]) => {
        if (showMore) {
          return [...subjects].sort(
            (a, b) => (b.isRelevant ? 1 : 0) - (a.isRelevant ? 1 : 0),
          );
        }
        return subjects.filter((s) => s.isRelevant);
      }),
    );

  protected readonly hasHiddenTier2$: Observable<boolean> =
    this.filteredTier2Subjects$.pipe(
      map((subjects) => subjects.some((s) => !s.isRelevant)),
    );

  // ============================================
  // STATE DROPDOWN
  // ============================================

  private readonly stateSearchText$$ = new BehaviorSubject<string>('');
  protected readonly stateSearchText$ = this.stateSearchText$$.asObservable();
  private readonly stateDropdownOpen$$ = new BehaviorSubject<boolean>(false);
  protected readonly stateDropdownOpen$ =
    this.stateDropdownOpen$$.asObservable();
  private readonly stateDropdownOpenUp$$ = new BehaviorSubject<boolean>(false);
  protected readonly stateDropdownOpenUp$ =
    this.stateDropdownOpenUp$$.asObservable();

  protected readonly filteredStates$: Observable<UsState[]> =
    this.stateSearchText$.pipe(
      map((searchText) => {
        if (!searchText.trim()) {
          return this.usStates;
        }
        const search = searchText.toLowerCase();
        return this.usStates.filter(
          (state) =>
            state.name.toLowerCase().includes(search) ||
            state.code.toLowerCase().includes(search),
        );
      }),
    );

  protected readonly selectedStateName$: Observable<string | null> =
    this.inputData$.pipe(
      map((data) => {
        if (!data.state) return null;
        const state = this.usStates.find((s) => s.code === data.state);
        return state?.name || null;
      }),
    );

  // ============================================
  // LIFECYCLE
  // ============================================

  ngOnInit(): void {
    this.loadMetadata();
    this.applyQueryParamPreselection();

    const assignmentId = this.assignmentService.assignmentId;
    this.amplitudeService.trackEvent('assignment_wizard_context_entered', {
      assignment_id: assignmentId,
      step_number: 1,
      step_name: 'context',
      is_new: !assignmentId,
      assignment_status: this.assignmentService.status,
    });
  }

  private loadMetadata(): void {
    this.metadataApiService
      .getMetadata()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((metadata) => {
        this.grades$$.next(metadata.grades);
        this.tier1Subjects$$.next(metadata.tier1Subjects);

        // After metadata loads, expand "See more" for tier1 subjects
        // if the pre-selected tier1 from query params is not relevant for the grade
        this.expandTier1IfPreselectedHidden(metadata.tier1Subjects);
      });
  }

  /**
   * Read grade and tier1SubjectId from query parameters (e.g. from the assignment-landing page)
   * and pre-populate the assignment state so the wizard starts with these values pre-selected.
   */
  private applyQueryParamPreselection(): void {
    const queryParams = this.route.snapshot.queryParams;
    const gradeParam = queryParams['grade'];
    const tier1SubjectIdParam = queryParams['tier1SubjectId'];

    if (!gradeParam && !tier1SubjectIdParam) {
      return;
    }

    const updates: Partial<{
      grade: number | null;
      tier1SubjectId: string | null;
    }> = {};

    if (gradeParam) {
      const gradeValue = parseInt(gradeParam, 10);
      if (!isNaN(gradeValue)) {
        updates.grade = gradeValue;

        // Expand "Show more" for grades if pre-selected grade is in the hidden K-5 range
        if (HIDDEN_GRADE_VALUES.includes(gradeValue)) {
          this.showMoreGrades$$.next(true);
        }
      }
    }

    if (tier1SubjectIdParam) {
      updates.tier1SubjectId = tier1SubjectIdParam;
    }

    this.assignmentService.updateInputData(updates);
  }

  /**
   * After metadata loads, check if the pre-selected tier1 subject (from query params)
   * would be hidden behind "See more" and expand it if so.
   */
  private expandTier1IfPreselectedHidden(
    tier1Subjects: Tier1Subject[],
  ): void {
    const inputData = this.assignmentService.inputData;

    if (!inputData.tier1SubjectId || inputData.grade === null) {
      return;
    }

    const tier1 = tier1Subjects.find(
      (t) => t.id === inputData.tier1SubjectId,
    );
    const isRelevant =
      tier1?.subjects.some((s) =>
        s.applicableGrades.includes(inputData.grade!),
      ) ?? false;

    if (!isRelevant) {
      this.showMoreTier1$$.next(true);
    }
  }

  // ============================================
  // GRADE METHODS
  // ============================================

  protected toggleShowMoreGrades(): void {
    this.showMoreGrades$$.next(!this.showMoreGrades$$.value);
  }

  protected updateGrade(value: number): void {
    const currentData = this.assignmentService.inputData;
    const tier1Subjects = this.tier1Subjects$$.value;

    // Check if current tier1 selection is still valid for the new grade
    let newTier1SubjectId = currentData.tier1SubjectId;
    let newTier2SubjectId = currentData.tier2SubjectId;

    if (newTier1SubjectId) {
      const tier1 = tier1Subjects.find((t) => t.id === newTier1SubjectId);
      const tier1HasValidSubjects = tier1?.subjects.some((s) =>
        s.applicableGrades.includes(value),
      );
      if (!tier1HasValidSubjects) {
        newTier1SubjectId = null;
        newTier2SubjectId = null;
      } else if (newTier2SubjectId) {
        const tier2 = tier1?.subjects.find((s) => s.id === newTier2SubjectId);
        if (!tier2?.applicableGrades.includes(value)) {
          newTier2SubjectId = null;
        }
      }
    }

    this.assignmentService.updateInputData({
      grade: value,
      tier1SubjectId: newTier1SubjectId,
      tier2SubjectId: newTier2SubjectId,
    });

    this.showMoreTier1$$.next(false);
    this.showMoreTier2$$.next(false);
  }

  // ============================================
  // SUBJECT METHODS
  // ============================================

  protected updateTier1Subject(id: string): void {
    const currentData = this.assignmentService.inputData;
    if (currentData.tier1SubjectId === id) {
      this.assignmentService.updateInputData({
        tier1SubjectId: null,
        tier2SubjectId: null,
      });
      this.assignmentService.resolvedSubjectName = null;
    } else {
      this.assignmentService.updateInputData({
        tier1SubjectId: id,
        tier2SubjectId: null,
      });
      this.assignmentService.resolvedSubjectName = null;
    }
    this.showMoreTier2$$.next(false);
  }

  protected updateTier2Subject(id: string): void {
    const currentData = this.assignmentService.inputData;
    if (currentData.tier2SubjectId === id) {
      this.assignmentService.updateInputData({ tier2SubjectId: null });
      this.assignmentService.resolvedSubjectName = null;
    } else {
      this.assignmentService.updateInputData({ tier2SubjectId: id });
      // Resolve human-readable name for the backend
      const tier1 = this.tier1Subjects$$.value.find(
        (t) => t.id === currentData.tier1SubjectId,
      );
      const tier2 = tier1?.subjects.find((s) => s.id === id);
      this.assignmentService.resolvedSubjectName = tier2?.name ?? null;
    }
  }

  protected toggleShowMoreTier1(): void {
    this.showMoreTier1$$.next(!this.showMoreTier1$$.value);
  }

  protected toggleShowMoreTier2(): void {
    this.showMoreTier2$$.next(!this.showMoreTier2$$.value);
  }

  // ============================================
  // STATE METHODS
  // ============================================

  protected onStateSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.stateSearchText$$.next(input.value);
    if (!this.stateDropdownOpen$$.value) {
      this.stateDropdownOpen$$.next(true);
    }
  }

  protected openStateDropdown(): void {
    if (this.stateComboboxRef) {
      const rect = this.stateComboboxRef.nativeElement.getBoundingClientRect();
      const dropdownHeight = 240;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const shouldOpenUp =
        spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
      this.stateDropdownOpenUp$$.next(shouldOpenUp);
    }
    this.stateDropdownOpen$$.next(true);
  }

  protected closeStateDropdown(): void {
    setTimeout(() => {
      this.stateDropdownOpen$$.next(false);
      this.stateSearchText$$.next('');
    }, 150);
  }

  protected selectState(state: UsState): void {
    this.assignmentService.updateInputData({ state: state.code });
    this.stateDropdownOpen$$.next(false);
    this.stateSearchText$$.next('');
  }

  protected clearState(event: Event): void {
    event.stopPropagation();
    this.assignmentService.updateInputData({ state: null });
    this.stateSearchText$$.next('');
  }

  // ============================================
  // NAVIGATION
  // ============================================

  protected onContinue(): void {
    this.assignmentService
      .createAssignmentContext()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          const inputData = this.assignmentService.inputData;
          this.amplitudeService.trackEvent(
            'assignment_wizard_step_context_completed',
            {
              assignment_id: this.assignmentService.assignmentId,
              grade: inputData.grade,
              tier1_subject: inputData.tier1SubjectId,
              tier2_subject: inputData.tier2SubjectId,
              state: inputData.state,
            },
          );

          const assignmentId = this.assignmentService.assignmentId;
          if (assignmentId) {
            this.assignmentService.setStep(2);
            this.router.navigate(['/assignment', assignmentId]).then(() => {
              this.scrollToTopWithRepaint();
            });
          } else {
            this.assignmentService.setStep(2);
            this.scrollToTopWithRepaint();
          }
        },
        error: (err: any) => {
          this.amplitudeService.trackEvent('assignment_wizard_error', {
            assignment_id: this.assignmentService.assignmentId,
            error_type: 'context_creation_failed',
            error: err?.message || 'Unknown error',
            step: 1,
          });
        },
      });
  }

  protected onExit(): void {
    this.router.navigate(['/teacher/dashboard']);
  }

  private scrollToTopWithRepaint(): void {
    const isSafariMobile =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.userAgent.includes('Safari') && 'ontouchstart' in window);

    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: isSafariMobile ? 'instant' : 'smooth',
      });

      if (isSafariMobile) {
        requestAnimationFrame(() => {
          document.body.style.transform = 'translateZ(0)';
          requestAnimationFrame(() => {
            document.body.style.transform = '';
          });
        });
      }
    }, 0);
  }
}
